// Permission catalog: THE list of permission keys (P1 §3.5). Seeded into core.permissions by migration and
// contract-checked (check-contracts fails if the table and this file differ). Semantics ported from India
// auth/require_auth.js:100-130: any-of, and role `owner` passes every check.
//
// Adding a key (other plans): append to PERMISSIONS in a small additive edit, add it to the default roles that need it,
// then add a migration in your range that runs `SELECT core.sync_permission(key, area, description)` — or simply rerun
// check-contracts, which prints the SQL to run. Keys are `<area>.<verb>`, lowercase snake.

/** @type {ReadonlyArray<{key:string, area:string, description:string}>} */
export const PERMISSIONS = Object.freeze([
  // jobs
  { key: 'jobs.view', area: 'jobs', description: 'See jobs and the board' },
  { key: 'jobs.create', area: 'jobs', description: 'Create jobs' },
  { key: 'jobs.edit', area: 'jobs', description: 'Edit job details and notes' },
  { key: 'jobs.assign', area: 'jobs', description: 'Assign and reassign technicians' },
  { key: 'jobs.cancel', area: 'jobs', description: 'Cancel jobs' },
  { key: 'jobs.override_status', area: 'jobs', description: 'Force a status change (reason required, audited)' },
  { key: 'jobs.record_offline_payment', area: 'jobs', description: 'Mark a job paid offline (CASH TAKEN)' },
  // leads
  { key: 'leads.view', area: 'leads', description: 'See leads' },
  { key: 'leads.claim', area: 'leads', description: 'Claim and work leads' },
  // customers & estimates
  { key: 'customers.view', area: 'customers', description: 'See customers and vehicles' },
  { key: 'customers.edit', area: 'customers', description: 'Edit customers and vehicles' },
  { key: 'estimates.send', area: 'estimates', description: 'Create and send estimates' },
  { key: 'estimates.approve_on_behalf', area: 'estimates', description: 'Record a customer approval (phone/in person)' },
  // catalog
  { key: 'catalog.manage', area: 'catalog', description: 'Edit the price book and import price lists' },
  // technicians & users
  { key: 'technicians.manage', area: 'technicians', description: 'Edit technicians, shifts and time off' },
  { key: 'technicians.onboard', area: 'technicians', description: 'Invite technicians' },
  { key: 'users.manage', area: 'users', description: 'Invite users and edit roles' },
  { key: 'users.status_view', area: 'users', description: 'See who is online' },
  // dispatch
  { key: 'dispatch.view_map', area: 'dispatch', description: 'Live dispatch map' },
  { key: 'dispatch.history_map', area: 'dispatch', description: 'Date-range history map' },
  // calls & comms
  { key: 'calls.listen', area: 'calls', description: 'Listen to call recordings and transcripts' },
  // money
  { key: 'payments.view', area: 'payments', description: 'See payments and invoices' },
  { key: 'payouts.manage', area: 'payments', description: 'Record and settle technician payouts' },
  { key: 'invoices.manage', area: 'payments', description: 'Create, edit and void invoices' },
  // parts
  { key: 'parts.view', area: 'parts', description: 'See parts and prices' },
  { key: 'parts.order', area: 'parts', description: 'Place parts orders' },
  { key: 'inventory.manage', area: 'parts', description: 'Adjust van and shop inventory' },
  { key: 'vendors.manage', area: 'parts', description: 'Manage vendors' },
  { key: 'parts.price', area: 'parts', description: 'Pin part prices and run the pricing engine (P7)' },
  { key: 'passport.manage', area: 'passport', description: 'Warranty terms, passport links, claims (P7)' },
  { key: 'fleet.manage', area: 'fleet', description: 'Fleet clients, PM plans, batches, portal invites (P7)' },
  // analytics & admin
  { key: 'analytics.view', area: 'analytics', description: 'Reports and analytics' },
  { key: 'notifications.manage', area: 'settings', description: 'Notification rules' },
  { key: 'settings.manage', area: 'settings', description: 'Company settings, locations, phone numbers' },
  { key: 'billing.manage', area: 'settings', description: 'Our subscription and billing' },
  { key: 'integrations.manage', area: 'settings', description: 'Connect shop software and data imports' },
  // dealership CRM (work_packages/crm/00_CORE_PLAN.md §3.9): reserved on day 1 for the core and all five products
  { key: 'customers.merge', area: 'customers', description: 'Merge duplicate customers' },
  { key: 'customers.export', area: 'customers', description: 'Export customer lists (CSV)' },
  { key: 'tasks.work', area: 'tasks', description: 'See own and own-department tasks; claim and complete them' },
  { key: 'tasks.assign', area: 'tasks', description: 'Assign tasks to others and see every queue' },
  { key: 'appointments.confirm', area: 'appointments', description: 'Confirm, reschedule and cancel appointments and mark arrival' },
  { key: 'group.view', area: 'group', description: 'Group overview across the stores a person works in' },
  { key: 'messages.send', area: 'messages', description: 'Send texts to customers (two-way texting)' },
  { key: 'campaigns.view', area: 'campaigns', description: 'See campaigns and their results' },
  { key: 'campaigns.manage', area: 'campaigns', description: 'Build campaigns and their templates' },
  { key: 'campaigns.send', area: 'campaigns', description: 'Approve, start and pause a campaign' },
  { key: 'deals.view', area: 'deals', description: 'See deals: own and unassigned only, unless the person also has deals.manage' },
  { key: 'deals.edit', area: 'deals', description: 'Work deals: stages, notes, tasks, trade-ins' },
  { key: 'deals.manage', area: 'deals', description: 'Desk manager: every deal, desk notes, assign and reassign' },
  { key: 'vehicle_inventory.view', area: 'vehicle_inventory', description: 'See the vehicles for sale (live inventory feed)' },
  { key: 'vehicle_inventory.manage', area: 'vehicle_inventory', description: 'Manage inventory feeds and what the assistant may say about a unit' },
]);

export const PERMISSION_KEYS = Object.freeze(PERMISSIONS.map((p) => p.key));

/**
 * India dashboard / backend keys → US keys. `hasPermission('leads_view')` in a ported component resolves through this
 * map (every key used in /home/lk/website/dashboard/src has an entry; check-contracts verifies the targets exist).
 * India-only features with no US equivalent map to the closest gate (noted).
 */
export const INDIA_ALIASES = Object.freeze({
  admin_view: ['analytics.view'],
  override_status: ['jobs.override_status'],
  payouts: ['payouts.manage'],
  vendor_management: ['vendors.manage'],
  mechanic_onboarding: ['technicians.onboard'],
  mechanics_management: ['technicians.manage'],
  add_mechanic: ['technicians.onboard'],
  franchise_leads: ['leads.view'],              // India franchise-lead tab → leads
  employee_management: ['users.manage'],
  employee_status_view: ['users.status_view'],
  date_range_map: ['dispatch.history_map'],
  mechanic_analytics: ['analytics.view'],
  notifications: ['notifications.manage'],
  mechanic_inventory: ['inventory.manage'],
  'spare-parts': ['parts.view'],
  'place-spare-orders': ['parts.order'],
  'spare-qr': ['inventory.manage'],
  gst_spares: ['parts.view'],                   // India GST view; US has no GST
  invoices: ['invoices.manage'],
  google_map_extractor: ['settings.manage'],    // India-internal tool; no US surface
  leads_view: ['leads.view'],
});

/** Resolve a key (US or India alias) to US keys. */
export function resolvePermission(key) {
  if (PERMISSION_KEYS.includes(key)) return [key];
  return INDIA_ALIASES[key] ? [...INDIA_ALIASES[key]] : [];
}

/** Role keys and their default permissions, seeded per tenant (core.seed_roles). `owner` bypasses every check. */
export const ROLE_KEYS = /** @type {const} */ (['owner', 'admin', 'dispatcher', 'csr', 'technician', 'accountant', 'viewer']);
const all = PERMISSION_KEYS;
export const DEFAULT_ROLES = Object.freeze({
  owner: { name: 'Owner', permissions: [...all] },
  admin: { name: 'Admin', permissions: all.filter((k) => k !== 'billing.manage') },
  dispatcher: { name: 'Dispatcher', permissions: ['jobs.view', 'jobs.create', 'jobs.edit', 'jobs.assign', 'jobs.cancel',
    'leads.view', 'leads.claim', 'customers.view', 'customers.edit', 'estimates.send', 'estimates.approve_on_behalf',
    'technicians.manage', 'users.status_view', 'dispatch.view_map', 'dispatch.history_map', 'calls.listen', 'payments.view',
    'parts.view', 'parts.order'] },
  csr: { name: 'Customer service', permissions: ['jobs.view', 'jobs.create', 'jobs.edit', 'leads.view', 'leads.claim',
    'customers.view', 'customers.edit', 'estimates.send', 'calls.listen', 'dispatch.view_map'] },
  technician: { name: 'Technician', permissions: ['parts.view'] }, // tech actions are authorized by assignment
  accountant: { name: 'Accountant', permissions: ['jobs.view', 'customers.view', 'payments.view', 'payouts.manage',
    'invoices.manage', 'jobs.record_offline_payment', 'analytics.view', 'parts.view', 'vendors.manage'] },
  viewer: { name: 'Viewer', permissions: ['jobs.view', 'customers.view', 'leads.view', 'dispatch.view_map', 'analytics.view'] },
});

/**
 * Dealership role templates (core.role_templates.segment = 'dealership'; seeded only into dealership stores). A
 * dealership also gets the segment-free templates above: owner (dealer principal: everything), admin (GM: everything
 * but billing) and viewer. The products use these keys and roles; they add no role (00_CORE_PLAN.md §8 rule 5).
 */
export const DEALER_SEGMENT = 'dealership';
export const DEALER_ROLE_KEYS = /** @type {const} */ (['service_manager', 'service_advisor', 'bdc_agent', 'sales_manager', 'salesperson']);
const CUSTOMERS_ALL = ['customers.view', 'customers.edit', 'customers.merge', 'customers.export'];
export const DEALER_ROLES = Object.freeze({
  service_manager: { name: 'Service manager', permissions: [...CUSTOMERS_ALL, 'leads.view', 'tasks.work', 'tasks.assign',
    'appointments.confirm', 'calls.listen', 'analytics.view', 'messages.send', 'campaigns.view', 'campaigns.manage', 'campaigns.send'] },
  service_advisor: { name: 'Service advisor', permissions: ['customers.view', 'customers.edit', 'leads.view', 'tasks.work',
    'appointments.confirm', 'calls.listen', 'messages.send', 'vehicle_inventory.view'] },
  bdc_agent: { name: 'BDC agent', permissions: ['customers.view', 'customers.edit', 'leads.view', 'tasks.work', 'appointments.confirm',
    'calls.listen', 'messages.send', 'deals.view'] },
  sales_manager: { name: 'Sales manager', permissions: [...CUSTOMERS_ALL, 'leads.view', 'tasks.work', 'tasks.assign', 'appointments.confirm',
    'calls.listen', 'deals.view', 'deals.edit', 'deals.manage', 'vehicle_inventory.view', 'vehicle_inventory.manage', 'messages.send',
    'analytics.view', 'campaigns.view', 'campaigns.send'] },
  salesperson: { name: 'Salesperson', permissions: ['customers.view', 'customers.edit', 'leads.view', 'tasks.work', 'appointments.confirm',
    'deals.view', 'deals.edit', 'vehicle_inventory.view', 'messages.send'] },
});
/** The roles a dealership's Team screen offers, in display order. */
export const DEALER_TEAM_ROLES = /** @type {const} */ (['owner', 'admin', ...DEALER_ROLE_KEYS, 'viewer']);

/**
 * What one of OUR staff may do inside a store under a `desk_support` grant (api/src/core/auth/store-access.js): look,
 * and listen. No edit, no money, no settings, no users. Not a tenant role: it is never seeded into core.roles.
 */
export const DESK_SUPPORT_PERMISSIONS = Object.freeze(['customers.view', 'jobs.view', 'leads.view', 'calls.listen']);

/**
 * Any-of check. Port of requirePermission semantics: owner passes; keys may be US or India aliases.
 * @param {{role:string, perms:Set<string>|string[]}} principal @param {string|string[]} keys
 */
export function hasPermission(principal, keys) {
  if (!principal) return false;
  if (principal.role === 'owner') return true;
  const have = principal.perms instanceof Set ? principal.perms : new Set(principal.perms || []);
  return [].concat(keys).some((k) => resolvePermission(k).some((u) => have.has(u)));
}
